import React, { useEffect, useState } from 'react';
import { Edit, ArrowBack, Add, PictureAsPdf, UploadFile, Delete } from "@mui/icons-material";
import styles from '../../assets/styles/LayoutShared.module.css';
import viewStyles from '../../assets/styles/LayoutSharedView.module.css';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import PescadorForm from './PescadorForm';
import RegistroForm from '../Registros/RegistroForm';
import InterrupcaoForm, { MOTIVOS_INTERRUPCAO, CONDICOES_AMBIENTAIS, FONTES_INFORMACAO, rotuloDe, formatarDiaBR } from './InterrupcaoForm';

const NOMES_MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const hoje = new Date();

const PescadorView = () => {
  const navigate = useNavigate();
  const { uid } = useParams();
  const [data, setData] = useState<any>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [registroFormOpen, setRegistroFormOpen] = useState(false);
  // Períodos sem atividade pesqueira: interrupção da atividade (não é pescaria com captura zero).
  const [interrupcoes, setInterrupcoes] = useState<any[]>([]);
  const [interrupcaoFormOpen, setInterrupcaoFormOpen] = useState(false);
  const [interrupcaoEmEdicao, setInterrupcaoEmEdicao] = useState<any>(null);
  const [fotoAmpliada, setFotoAmpliada] = useState<any>(null);
  const [relatorioAberto, setRelatorioAberto] = useState(false);
  const [relatorioMes, setRelatorioMes] = useState(hoje.getMonth() + 1);
  const [relatorioAno, setRelatorioAno] = useState(hoje.getFullYear());
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);
  // Pescador com muito tempo de casa acumula centenas de registros — sem
  // limite, a tabela inteira ia pro DOM de uma vez e travava a página.
  // Mostra só os mais recentes por padrão, com opção de expandir.
  const [mostrarTodosRegistros, setMostrarTodosRegistros] = useState(false);
  const LIMITE_REGISTROS_INICIAL = 20;

const formatarDataBR = (dataRaw: string) => {
  if (!dataRaw) return '-';
  
  // Criamos o objeto Date, mas usamos os métodos UTC para extrair os valores reais do banco
  const d = new Date(dataRaw);
  const dia = String(d.getUTCDate()).padStart(2, '0');
  const mes = String(d.getUTCMonth() + 1).padStart(2, '0');
  const ano = d.getUTCFullYear();
  
  return `${dia}/${mes}/${ano}`;
};

  const fetchPescador = () => {
    if (!uid) return;
    api.get(`/pesca/pescadores/${uid}`).then(res => setData(res.data.data));
  };

  const fetchInterrupcoes = () => {
    if (!uid) return;
    api.get(`/pesca/pescadores/${uid}/interrupcoes`).then(res => setInterrupcoes(res.data.data || []));
  };

  const handleExcluirInterrupcao = async (item: any) => {
    const periodo = item.em_andamento
      ? `desde ${formatarDiaBR(item.data_inicio)} (em andamento)`
      : `de ${formatarDiaBR(item.data_inicio)} a ${formatarDiaBR(item.data_fim)}`;
    if (!window.confirm(`Excluir o período sem atividade ${periodo}?`)) return;
    try {
      await api.delete(`/pesca/interrupcoes/${item.uid_pesca_interrupcao_atividade}`);
      fetchInterrupcoes();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Não foi possível excluir o período.');
    }
  };

  useEffect(() => {
    fetchPescador();
    fetchInterrupcoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const handleSalvo = () => {
    setFormOpen(false);
    fetchPescador();
  };

  // Relatório mensal de produção (Gatilho 1) — gerado 100% em memória no
  // backend a cada pedido, nunca salvo em lugar nenhum; aqui só baixa o PDF
  // que a API devolve.
  const handleGerarRelatorio = async () => {
    if (!uid) return;
    setGerandoRelatorio(true);
    try {
      const res = await api.get(`/pesca/pescadores/${uid}/relatorio-mensal`, {
        params: { mes: relatorioMes, ano: relatorioAno },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-mensal-${String(relatorioMes).padStart(2, '0')}-${relatorioAno}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setRelatorioAberto(false);
    } catch (err: any) {
      let mensagem = 'Não foi possível gerar o relatório.';
      if (err?.response?.data instanceof Blob) {
        try {
          const texto = await err.response.data.text();
          mensagem = JSON.parse(texto)?.message || mensagem;
        } catch {
          // mantém a mensagem padrão se o corpo do erro não vier em JSON
        }
      }
      alert(mensagem);
    } finally {
      setGerandoRelatorio(false);
    }
  };

  // Função de cálculo reutilizada do RegistroView
  const calcularPesoConvertido = (peso: string | number | null, apresentacao: string) => {
    if (!peso) return 0;
    const p = parseFloat(peso.toString()) || 0;
    if (apresentacao === 'eviscerado') return p * 1.2;
    // Banco/formulário usam "filé" (com acento, ver RegistroForm.tsx) —
    // comparar com "file" nunca batia, então captura em filé caía no
    // fallback "bruto" (sem o fator ×3) por engano.
    if (apresentacao === 'filé') return p * 3;
    return p;
  };

  if (!data) return <div className={viewStyles.container}>Carregando...</div>;

  const enderecoCompleto = [data.logradouro, data.numero, data.bairro]
    .filter(Boolean)
    .join(', ');

  const registrosOrdenados: any[] = (data.registros || [])
    .slice()
    .sort((a: any, b: any) => new Date(b.data_inicio).getTime() - new Date(a.data_inicio).getTime());
  const registrosExibidos = mostrarTodosRegistros
    ? registrosOrdenados
    : registrosOrdenados.slice(0, LIMITE_REGISTROS_INICIAL);

  return (
    <div className={viewStyles.container}>
      <div className={viewStyles.headerTitle}>
        <h1 className={viewStyles.greenTitle}>Cadastro de Pescador</h1>
        <div className={viewStyles.actionsHeader} style={{ position: 'relative' }}>
          <button className={viewStyles.blueBtn} onClick={() => setRelatorioAberto((v) => !v)} title="Gerar Relatório Mensal">
            <PictureAsPdf fontSize="small" />
          </button>
          <button className={viewStyles.blueBtn} onClick={() => setFormOpen(true)}><Edit fontSize="small" /></button>
          <button className={viewStyles.blueBtn} onClick={() => navigate('/admin/pescadores')}><ArrowBack fontSize="small" /></button>

          {relatorioAberto && (
            <div
              style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 10,
                background: '#fff', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
                padding: '14px', width: '260px',
              }}
            >
              <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: '0.9rem' }}>Gerar Relatório Mensal</p>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <select
                  value={relatorioMes}
                  onChange={(e) => setRelatorioMes(Number(e.target.value))}
                  style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #ccc' }}
                >
                  {NOMES_MESES.map((nome, idx) => (
                    <option key={nome} value={idx + 1}>{nome}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={relatorioAno}
                  onChange={(e) => setRelatorioAno(Number(e.target.value))}
                  style={{ width: '90px', padding: '8px', borderRadius: '8px', border: '1px solid #ccc' }}
                />
              </div>
              <button
                className={viewStyles.blueBtn}
                onClick={handleGerarRelatorio}
                disabled={gerandoRelatorio}
                style={{ width: '100%' }}
              >
                {gerandoRelatorio ? 'Gerando...' : 'Baixar PDF'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* CARD DE DADOS GERAIS DO PESCADOR */}
      <div className={viewStyles.sectionCard}>
        <div className={viewStyles.sectionHeader}>DADOS DO PESCADOR</div>
        <div className={viewStyles.sectionBody}>
          <div className={viewStyles.gridInfo}>
            <div><label>Nome</label><p>{data.nome}</p></div>
            <div><label>CPF</label><p>{data.cpf || 'Não informado'}</p></div>
            <div><label>Colônia</label><p>{data.colonia?.nome || 'Não vinculado'}</p></div>
            <div><label>Cidade / UF</label><p>{data.municipio?.municipio || '-'} / {data.id_estado || '-'}</p></div>
            <div><label>Telefone</label><p>{data.telefone || '-'}</p></div>
            <div><label>Registro</label><p>{data.registro_pescador || '-'}</p></div>
            <div><label>Endereço</label><p>{enderecoCompleto || '-'}{data.complemento ? ` (${data.complemento})` : ''}</p></div>
            <div><label>CEP</label><p>{data.cep || '-'}</p></div>
            <div>
              <label>Projeto(s)</label>
              <p>
                {data.projetoVinculos && data.projetoVinculos.length > 0
                  ? data.projetoVinculos.map((v: any) => v.projeto?.nome).filter(Boolean).join(', ')
                  : 'Nenhum'}
              </p>
            </div>
            <div>
              <label>Área(s) de Pesca</label>
              <p>
                {data.areaVinculos && data.areaVinculos.length > 0
                  ? data.areaVinculos.map((v: any) => v.area?.nome).filter(Boolean).join(', ')
                  : 'Nenhuma'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* TABELA DE HISTÓRICO DE REGISTROS - CONSULTA APENAS */}
      <div className={viewStyles.sectionCard}>
        <div className={viewStyles.sectionHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>REGISTROS DE PESCA</span>
          <div style={{ display: 'flex', gap: '12px' }}>
            <UploadFile
              fontSize="small"
              style={{ cursor: 'pointer' }}
              titleAccess="Importar ficha de campo"
              onClick={() => navigate(`/admin/pescadores/view/${uid}/importar-ficha`)}
            />
            <Add
              fontSize="small"
              style={{ cursor: 'pointer' }}
              titleAccess="Nova Pescaria"
              onClick={() => setRegistroFormOpen(true)}
            />
          </div>
        </div>
        <div className={viewStyles.tableWrapper} style={{ marginTop: '0' }}>
          <table className={styles.table}>
            <thead className={styles.tableSubHeader}>
              <tr>
                <th style={{ width: '15%' }}>Data</th>
                <th style={{ width: '40%' }}>Local</th>
                <th style={{ width: '20%', textAlign: 'right' }}>Esforço</th>
                {/* Ajuste de whiteSpace para impedir quebra de linha no título */}
                <th style={{ width: '25%', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  Peso Convertido (Total)
                </th>
              </tr>
            </thead>
            <tbody>
              {registrosExibidos.length > 0 ? (
                registrosExibidos
      .map((reg: any) => {
        const totalReg = reg.itens?.reduce((acc: number, i: any) => 
          acc + calcularPesoConvertido(i.peso_kg, i.apresentacao), 0) || 0;

                  return (
                    <tr key={reg.uid_pesca_registro} onClick={() => navigate(`/admin/registros/view/${reg.uid_pesca_registro}`)} style={{ cursor: 'pointer' }}>
                      <td>{formatarDataBR(reg.data_inicio)}</td>
                      <td>{reg.descricao_local || '-'}</td>
                      {/* CORREÇÃO: Adicionado num_embarcacoes para aparecer a qtd de barcos */}
                      <td style={{ textAlign: 'right' }}>
                        {reg.num_pescadores || 0} pesc. / {reg.num_embarcacoes || 0} barcos
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold', color: 'var(--green-700)', whiteSpace: 'nowrap' }}>
                        {totalReg.toFixed(2)} kg
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    Nenhum registro de pesca encontrado para este pescador.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {registrosOrdenados.length > LIMITE_REGISTROS_INICIAL && (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <button
              type="button"
              onClick={() => setMostrarTodosRegistros((v) => !v)}
              style={{
                border: 'none', background: 'none', color: 'var(--light-blue-dark)',
                fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              {mostrarTodosRegistros
                ? 'Mostrar só os últimos ' + LIMITE_REGISTROS_INICIAL
                : `Mostrar todos (${registrosOrdenados.length})`}
            </button>
          </div>
        )}

      </div>

      {/* PERÍODOS SEM ATIVIDADE PESQUEIRA */}
      <div className={viewStyles.sectionCard}>
        <div className={viewStyles.sectionHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>PERÍODOS SEM ATIVIDADE</span>
          <Add
            fontSize="small"
            style={{ cursor: 'pointer' }}
            titleAccess="Novo período sem atividade"
            onClick={() => { setInterrupcaoEmEdicao(null); setInterrupcaoFormOpen(true); }}
          />
        </div>
        <div className={viewStyles.tableWrapper} style={{ marginTop: '0' }}>
          <table className={styles.table}>
            <thead className={styles.tableSubHeader}>
              <tr>
                <th style={{ width: '26%' }}>Período</th>
                <th style={{ width: '12%', textAlign: 'right' }}>Dias</th>
                <th style={{ width: '20%' }}>Motivo</th>
                <th style={{ width: '32%' }}>Relato</th>
                <th style={{ width: '10%', textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {interrupcoes.length > 0 ? (
                interrupcoes.map((i: any) => (
                  <tr key={i.uid_pesca_interrupcao_atividade}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {i.em_andamento
                        ? `desde ${formatarDiaBR(i.data_inicio)} · em andamento`
                        : `${formatarDiaBR(i.data_inicio)} a ${formatarDiaBR(i.data_fim)}`}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {i.em_andamento ? `${i.dias} até hoje` : i.dias}
                    </td>
                    <td>
                      {rotuloDe(MOTIVOS_INTERRUPCAO, i.motivo)}
                      {i.motivo === 'ambiental' && i.condicao_ambiental ? ` — ${rotuloDe(CONDICOES_AMBIENTAIS, i.condicao_ambiental)}` : ''}
                    </td>
                    <td>
                      {i.relato || '-'}
                      <div style={{ fontSize: '0.75rem', color: '#999' }}>Fonte: {rotuloDe(FONTES_INFORMACAO, i.fonte_informacao)}</div>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Edit
                        fontSize="small"
                        style={{ cursor: 'pointer', marginRight: '8px' }}
                        titleAccess="Editar período"
                        onClick={() => { setInterrupcaoEmEdicao(i); setInterrupcaoFormOpen(true); }}
                      />
                      <Delete
                        fontSize="small"
                        style={{ cursor: 'pointer' }}
                        titleAccess="Excluir período"
                        onClick={() => handleExcluirInterrupcao(i)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    Nenhum período sem atividade registrado para este pescador.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* FOTOS ENVIADAS PELO PESCADOR (Epic 3, Task 3.6) */}
      <div className={viewStyles.sectionCard}>
        <div className={viewStyles.sectionHeader}>FOTOS</div>
        <div className={viewStyles.sectionBody}>
          {data.tbl_pesca_fotos && data.tbl_pesca_fotos.length > 0 ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: '12px',
              }}
            >
              {data.tbl_pesca_fotos.map((foto: any) => (
                <div
                  key={foto.uid_pesca_foto}
                  onClick={() => setFotoAmpliada(foto)}
                  style={{
                    cursor: 'pointer', borderRadius: '10px', overflow: 'hidden',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.12)', background: '#fff',
                  }}
                >
                  <div style={{ width: '100%', aspectRatio: '1 / 1', background: '#f0f0f0', overflow: 'hidden' }}>
                    <img src={foto.url} alt={foto.legenda || 'Foto'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  {foto.legenda && (
                    <div style={{ padding: '6px 8px', fontSize: '0.75rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {foto.legenda}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#999', margin: 0 }}>Nenhuma foto enviada por este pescador ainda.</p>
          )}
        </div>
      </div>

      {formOpen && uid && (
        <PescadorForm
          pescadorUid={uid}
          onClose={() => setFormOpen(false)}
          onSaved={handleSalvo}
        />
      )}

      {registroFormOpen && uid && (
        <RegistroForm
          pescadorFixo={{ uid_pesca_pescador: uid, nome: data.nome }}
          onClose={() => setRegistroFormOpen(false)}
          onSaved={() => { setRegistroFormOpen(false); fetchPescador(); }}
        />
      )}

      {interrupcaoFormOpen && uid && (
        <InterrupcaoForm
          pescadorFixo={{ uid_pesca_pescador: uid, nome: data.nome }}
          projetosVinculados={(data.projetoVinculos || []).map((v: any) => v.projeto?.uid_pesca_projeto).filter(Boolean)}
          interrupcao={interrupcaoEmEdicao}
          onClose={() => setInterrupcaoFormOpen(false)}
          onSaved={() => { setInterrupcaoFormOpen(false); fetchInterrupcoes(); }}
        />
      )}

      {fotoAmpliada && (
        <div
          onClick={() => setFotoAmpliada(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '20px',
          }}
        >
          <img
            src={fotoAmpliada.url}
            alt={fotoAmpliada.legenda || 'Foto'}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }}
          />
        </div>
      )}
    </div>
  );
};

export default PescadorView;
